import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import React from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  selectIsExpressUpsellMode,
  selectIsExpressTimerActive,
} from '../../selectors/menu';
import { selectFinalOrderSubmitted } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectIsCarPresent } from '../../selectors/message';
import {
  selectFeatureFlagHitl3,
  selectIsHitl7Enabled,
} from '../../redux/features/config/config.selector';
import {
  selectAIReusedAnswers,
  selectAIExpressModeActive,
} from '../../redux/features/ai/ai.selector';
import { ReusedAnswers } from '../../redux/features/ai/ai.props';
import { useReminder } from '../../contexts/ReminderContext';
import Colors from '../../utils/color';
import {
  HITL3_FONT_SIZE,
  HITL7_CHIP_FONT_SIZE,
  HITL7_CHIP_ICON_SIZE,
} from '../../utils/hitl3Typography';
import { HITL3_PADDING, HITL7_CHIP_GAP } from '../../utils/hitl3Spacing';

/**
 * PULSE SIGNALS (HITL 3.0) — a chip row of live order signals in the Pulse strip.
 *
 * Chip kinds, each shown only when active (no heading, no empty-state — an empty
 * row simply renders nothing so it takes no space):
 *   • HITL - Express Mode (amber) — express mode ON by either selector trigger
 *     (`isExpressUpsellMode || isExpressTimerActive`), dropped on car exit.
 *   • AI - Express Mode (amber) — the AI reports express mode active via
 *     `ai_session_state` (`express_mode.active`). Distinct from the
 *     selector-driven Express chip above.
 *   • Reminder (violet) — the live slash-command reminder (`useReminder`), folded
 *     in from the old Action Center. A deliberately distinct colour from the
 *     AI-suggestion box so it doesn't read as the ask.
 *   • Reused answers (teal) — the reused-answers payload from `ai_session_state`
 *     (HITL 3.0), formatted as "Reuse: Drink size - Large, …" for the answer
 *     slots the AI carried over. Shown only when something was reused.
 *
 * (Sentiment is NOT a chip — it's a fixed vitals-row tile, see SentimentTile.)
 *
 * More signals (intent, upsell state, …) can slot into the same row later.
 * Gated by hitl_3_0 at the call site (CustomerPulse); the ai_session_state chips
 * also self-gate on the flag.
 */

// Capitalize the first letter only (sentence case): "large" -> "Large".
const sentenceCase = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// Answer-slot key -> human label: "drink_size" -> "Drink size".
const humanizeKey = (key: string): string =>
  sentenceCase(key.replace(/_/g, ' '));

/**
 * Formats the ai_session_state `reused_answers` payload into the Pulse chip text.
 * Only entries flagged `reused: true` are shown; a slot with several reused
 * values joins them with " / ". Returns '' when there is nothing reused (empty
 * payload, all `reused: false`, or malformed) so the chip stays hidden.
 *
 *   { drink_size: [{ value: 'large', reused: true }] }
 *     -> "Reuse: Drink size - Large"
 *   { drink_size: [{ value: 'large', reused: true }],
 *     combo_size: [{ value: 'small', reused: true }] }
 *     -> "Reuse: Drink size - Large, Combo size - Small"
 */
export const formatReusedAnswers = (
  reusedAnswers: ReusedAnswers | null
): string => {
  if (!reusedAnswers || typeof reusedAnswers !== 'object') {
    return '';
  }
  const parts: string[] = [];
  Object.entries(reusedAnswers).forEach(([key, entries]) => {
    if (!Array.isArray(entries)) {
      return;
    }
    const values = entries
      .filter((e) => e && e.reused === true && typeof e.value === 'string')
      .map((e) => sentenceCase(e.value));
    if (values.length) {
      parts.push(`${humanizeKey(key)} - ${values.join(' / ')}`);
    }
  });
  return parts.length ? `Reuse: ${parts.join(', ')}` : '';
};
const useStyles = makeStyles((theme: Theme) => ({
  row: {
    marginTop: HITL3_PADDING,
    paddingLeft: HITL3_PADDING,
    paddingRight: HITL3_PADDING,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
  },
  // Express signal — amber pill.
  sigchip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.375, 1, 0.375, 0.875),
    borderRadius: 999,
    backgroundColor: '#fff7e0',
    border: '1px solid #ffe19a',
    color: '#8a5d00',
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
  },
  sigIcon: {
    fontSize: '0.85rem',
    color: Colors.amber,
    flexShrink: 0,
  },
  // Reminder — violet chip, distinct from both the amber signal and the
  // (blue-lavender) AI-suggestion box. Wraps for long reminders.
  remindChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.625),
    padding: theme.spacing(0.5, 1.125, 0.5, 0.875),
    borderRadius: 12,
    backgroundColor: '#f0e9fb',
    border: '1px solid #d6c4ef',
    color: '#6a34ac',
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
    lineHeight: 1.3,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  remindIcon: {
    fontSize: '0.85rem',
    color: '#7c4dc4',
    flexShrink: 0,
  },
  // Reused answers — teal chip. Wraps like the reminder chip since the summary
  // can run long.
  reusedChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.625),
    padding: theme.spacing(0.5, 1.125, 0.5, 0.875),
    borderRadius: 12,
    backgroundColor: '#e6f7f2',
    border: '1px solid #b8e6d8',
    color: '#0f7a5a',
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
    lineHeight: 1.3,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  reusedIcon: {
    fontSize: '0.85rem',
    color: '#12987a',
    flexShrink: 0,
  },
}));

// HITL-1663: 7.0 shrinks the chips to 12px. Inline overrides rather than new
// makeStyles rules — extra rules advance JSS's global class-name counter and
// renumber generated names in unrelated suites, for a change nobody with the
// flag off can see. Hoisted so they are not rebuilt on every render.
const CHIP_7_0: React.CSSProperties = {
  fontSize: HITL7_CHIP_FONT_SIZE,
  gap: HITL7_CHIP_GAP,
};
const CHIP_ICON_7_0: React.CSSProperties = { fontSize: HITL7_CHIP_ICON_SIZE };

const PulseSignals = () => {
  const classes = useStyles();
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const isExpressUpsellMode = useAppSelector(selectIsExpressUpsellMode);
  const isExpressTimerActive = useAppSelector(selectIsExpressTimerActive);
  const isCarPresent = useAppSelector(selectIsCarPresent);
  const finalOrderSubmitted = useAppSelector(selectFinalOrderSubmitted);
  const reusedAnswers = useAppSelector(selectAIReusedAnswers);
  const aiExpressModeActive = useAppSelector(selectAIExpressModeActive);
  const { reminderMessage, isVisible: reminderVisible } = useReminder();

  // Every Pulse chip is a per-order signal: only show while a car is present and
  // the order is still live, so none lingers past the interaction — all are
  // dropped on car exit or once the order is finished/cancelled. Each chip adds
  // its own trigger on top of that shared gate.
  const perOrderActive = isCarPresent && !finalOrderSubmitted;
  const expressActive =
    (isExpressUpsellMode || isExpressTimerActive) && perOrderActive;
  const showReminder = reminderVisible && !!reminderMessage && perOrderActive;

  // ai_session_state chips (HITL 3.0) — also self-gated on the flag. Reused
  // answers shows when the formatted summary is non-empty; AI express mode when
  // the AI reports it active. (Sentiment is a tile, not a chip — see SentimentTile.)
  const reusedSummary = formatReusedAnswers(reusedAnswers);
  const showReusedAnswers = isHitl3 && !!reusedSummary && perOrderActive;
  const showAIExpressMode = isHitl3 && aiExpressModeActive && perOrderActive;

  // Which trigger(s) put the merged 7.0 chip on screen. Express mode has three
  // sources with three DIFFERENT lifetimes, and the merged chip deliberately
  // hides which one is speaking:
  //   meal-period — time of day at the restaurant; correctly stays on across
  //                 cars, because it describes the hour, not the order
  //   timer       — this order ran long; per order
  //   ai          — the AI reported express mode; per session
  // So "the chip is still on after a new car" is only a bug for two of the
  // three. Exposed as a data attribute (invisible to the agent) so that
  // question can be answered from the element rather than guessed at.
  const expressSource = [
    isExpressUpsellMode && perOrderActive ? 'meal-period' : '',
    isExpressTimerActive && perOrderActive ? 'timer' : '',
    showAIExpressMode ? 'ai' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Empty row takes no space (no heading / placeholder).
  if (
    !expressActive &&
    !showAIExpressMode &&
    !showReminder &&
    !showReusedAnswers
  ) {
    return null;
  }

  return (
    <div
      className={classes.row}
      data-testid="pulse-signals"
      // HITL-1663: under 7.0 the Pulse strip IS the wrapping chip row, so this
      // wrapper steps out of the box tree (`display: contents`) and its chips
      // become direct flex items of the strip. Without it they would wrap as a
      // block of their own and the dial chip could never share their line.
      // The element stays in the DOM, which is what lets the strip count
      // children to decide whether it has anything to show at all.
      style={isHitl7 ? { display: 'contents' } : undefined}
    >
      {/* HITL-1663: 7.0 shows ONE express chip. The split into "HITL -" and
          "AI -" records which side detected express mode, which is a question
          about our plumbing, not about the order. The agent does the same thing
          either way, and two near-identical amber pills side by side cost a row
          of the strip to say one thing. Flag off, both chips render as before. */}
      {isHitl7
        ? (expressActive || showAIExpressMode) && (
            <span
              className={classes.sigchip}
              style={CHIP_7_0}
              data-testid="signal-express-mode"
              data-express-source={expressSource}
            >
              <BoltIcon className={classes.sigIcon} style={CHIP_ICON_7_0} />
              Express Mode
            </span>
          )
        : [
            expressActive && (
              <span
                key="hitl-express"
                className={classes.sigchip}
                data-testid="signal-express-mode"
              >
                <BoltIcon className={classes.sigIcon} />
                HITL - Express Mode
              </span>
            ),
            showAIExpressMode && (
              <span
                key="ai-express"
                className={classes.sigchip}
                data-testid="signal-ai-express-mode"
              >
                <BoltIcon className={classes.sigIcon} />
                AI - Express Mode
              </span>
            ),
          ]}
      {showReusedAnswers && (
        <span
          className={classes.reusedChip}
          style={isHitl7 ? CHIP_7_0 : undefined}
          data-testid="signal-reused-answers"
        >
          <CachedOutlinedIcon
            className={classes.reusedIcon}
            style={isHitl7 ? CHIP_ICON_7_0 : undefined}
          />
          <span>{reusedSummary}</span>
        </span>
      )}
      {showReminder && (
        <span
          className={classes.remindChip}
          style={isHitl7 ? CHIP_7_0 : undefined}
          data-testid="signal-reminder"
        >
          <NotificationsNoneOutlinedIcon
            className={classes.remindIcon}
            style={isHitl7 ? CHIP_ICON_7_0 : undefined}
          />
          <span>{reminderMessage}</span>
        </span>
      )}
    </div>
  );
};

export default PulseSignals;
